/**
 * The dashboards' label and tone maps — kept out of the components so fast refresh stays
 * intact (the `react-refresh` rule every `format.ts` sibling cites).
 *
 * `WORK_COLUMN_LABELS` mirrors `WORK_BOARD_COLUMN_LABELS` in
 * `packages/shared/src/work-board.ts`. The web imports ONLY TYPES from `@crf/shared`
 * (`lib/permissions.ts` explains the CommonJS reason), so the runtime copy lives here and
 * `labels-mirror.test.ts` holds it to the shared file as text. The work board itself never
 * needed this map — its route sends `label` with every column; the dashboard's
 * `work.by_column` sends counts by key only.
 */

import { QUOTE_STATUS_MAP, isQuoteStatus } from '../../domain/quotes';
import type { DocTone } from '../../domain/quotes';

export const WORK_COLUMN_LABELS: Readonly<Record<string, string>> = {
  yet_to_assign: 'Yet to Assign',
  in_development: 'In Development',
  correction: 'Correction',
  testing: 'Testing',
  ready_to_deploy: 'Ready to Deploy',
};

export function workColumnLabel(column: string): string {
  return WORK_COLUMN_LABELS[column] ?? column;
}

/**
 * A quote stage's TONE as a `text-*` utility — the same pairs `docToneClasses` paints the
 * status pills with (measured AA, mode-invariant), carried as `currentColor` into the SVG
 * marks and the legend dots. Every literal is spelled here so Tailwind's scanner sees it.
 */
const QUOTE_TONE_TEXT: Readonly<Record<DocTone, string>> = {
  neutral: 'text-fg-secondary',
  info: 'text-info-fg',
  success: 'text-ok-fg',
  warning: 'text-warn-fg',
  lost: 'text-stage-lost-fg',
  confirm: 'text-stage-confirm-fg',
};

/** Label + tone for a stage string off the wire; an unknown stage keeps the server's word, neutral. */
export function quoteStageFacts(stage: string): { label: string; toneClass: string } {
  if (isQuoteStatus(stage)) {
    const descriptor = QUOTE_STATUS_MAP[stage];
    return { label: descriptor.label, toneClass: QUOTE_TONE_TEXT[descriptor.tone] };
  }
  return { label: stage, toneClass: QUOTE_TONE_TEXT.neutral };
}

/** The five board columns, each with its own stage tone — distinct, but never the only carrier. */
export const WORK_COLUMN_TONE: Readonly<Record<string, string>> = {
  yet_to_assign: 'text-fg-secondary',
  in_development: 'text-stage-dev-fg',
  correction: 'text-stage-revision-fg',
  testing: 'text-stage-test-fg',
  ready_to_deploy: 'text-stage-deploy-fg',
};
